import { ComponentDataDestService } from '../component-data-dest.service';
import { catchError, firstValueFrom, map, of, switchMap, tap } from 'rxjs';
import {
    extractEventGuidsFromCreateResponse,
    patchEventCategoryForEventGuids,
} from './event/patch-event-category-from-form';
import { postEventMaterialRequestsForGuids } from './event/post-event-material-requests';
import { ComponentDataSourceService } from '../../component-data-source/component-data-source.service';
import { EventDTO } from '../../../dto/event/event.dto';
import { buildCoursePricingFromFormData, buildCoursePriceMetadataFromFormData, resolveEventPriceFromFormData } from '../../component-data-source/data/event/event_form_data';
import {
  applyEndTimeToStartDate,
  getScheduleMode,
  getSeriesShareParticipants,
  normalizeEventFormForApi,
} from '../../component-data-source/data/event/event-schedule-form.util';
import { mapFormCateringToApi } from '../../component-data-source/data/event/event-catering-field.util';
import {
  mapAdditionalRoomsToApi,
  mapOrganizationResourceIdsToApi,
  sanitizeAdditionalRoomsFormData,
} from '../../component-data-source/data/event/event-booking-form.util';
import { environment } from '../../../environment/environment';
import { HttpClient } from '@angular/common/http';
import { EventParticipantDTO } from '../../../dto/event/event-participant.dto';
import { OrganizationDTO } from '../../../dto/organization/organization.dto';
import { LocationDTO } from '../../../dto/location/location.dto';
import { RoomDTO } from '../../../dto/location/room.dto';
import { Store } from '@ngrx/store';
import * as EventsActions from '../../../ngrx/events/events.actions';
import { AuthService } from '../../auth/auth.service';

function resolveOrganizationIdFromAuth(componentDataDestService: ComponentDataDestService): number {
    try {
        const auth = componentDataDestService.injector.get(AuthService);
        const user = auth.getCurrentUser() as Record<string, unknown> | null;
        const orgId =
            user?.['organizations_id'] ??
            (user?.['user_info'] as Record<string, unknown> | undefined)?.['organizations_id'];
        const n = typeof orgId === 'number' ? orgId : parseInt(String(orgId ?? ''), 10);
        return Number.isFinite(n) && n > 0 ? n : 0;
    } catch {
        return 0;
    }
}

// Helper function to format Date as local datetime string (preserves local time)
function formatLocalDateTime(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

/** datetime-local value (YYYY-MM-DDTHH:mm) for add-event form defaults. */
function toDateTimeLocalValue(date: Date): string {
    return formatLocalDateTime(date).slice(0, 16);
}

/** Default start/end datetimes when creating an event on a selected calendar day or timeslot. */
export function buildNewEventDateDefaults(date: Date, end?: Date): { start_date: string; end_date: string } {
    const durationMs = end instanceof Date && !Number.isNaN(end.getTime())
        ? end.getTime() - date.getTime()
        : NaN;
    const isTimedSlot = Number.isFinite(durationMs) && durationMs > 0 && durationMs <= 2 * 60 * 60 * 1000;
    const hasClockTime = date.getHours() !== 0 || date.getMinutes() !== 0;

    if (isTimedSlot) {
        return {
            start_date: toDateTimeLocalValue(date),
            end_date: toDateTimeLocalValue(end as Date),
        };
    }

    if (hasClockTime) {
        const slotEnd = end instanceof Date && end.getTime() > date.getTime()
            ? end
            : new Date(date.getTime() + 60 * 60 * 1000);
        return {
            start_date: toDateTimeLocalValue(date),
            end_date: toDateTimeLocalValue(slotEnd),
        };
    }

    const start = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
    const defaultEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 13, 0, 0, 0);
    return {
        start_date: toDateTimeLocalValue(start),
        end_date: toDateTimeLocalValue(defaultEnd),
    };
}

export function add_event(componentDataDestService: ComponentDataDestService, componentDataSourceService: ComponentDataSourceService, data: any) {
    const categoryPatchData: Record<string, unknown> = {};
    if (data != null && typeof data === 'object' && 'event_category' in data) {
        categoryPatchData['event_category'] = (data as Record<string, unknown>)['event_category'];
    }
    data = normalizeEventFormForApi(data);
    sanitizeAdditionalRoomsFormData(data);
    const eventDurationType = getScheduleMode(data);

    const eventType = data.event_type;
    if (!eventType || typeof eventType !== 'string' || !eventType.trim()) {
        throw new Error('Vælg begivenhedstype (Type) før du gemmer.');
    }
    if (!data.visibility || typeof data.visibility !== 'string') {
        throw new Error('Vælg synlighed før du gemmer.');
    }
    
    // CRITICAL: Extract organization ID from multiple possible sources and ensure it's an integer
    let organizationId = 0;
    
    // Try multiple possible sources for organization ID
    if (data.organization_id !== undefined && data.organization_id !== null) {
        organizationId = typeof data.organization_id === 'string' ? parseInt(data.organization_id, 10) || 0 : (data.organization_id || 0);
    } else if (data.organizations_id !== undefined && data.organizations_id !== null) {
        organizationId = typeof data.organizations_id === 'string' ? parseInt(data.organizations_id, 10) || 0 : (data.organizations_id || 0);
    } else if (data.organization && data.organization.id !== undefined && data.organization.id !== null) {
        organizationId = typeof data.organization.id === 'string' ? parseInt(data.organization.id, 10) || 0 : (data.organization.id || 0);
    }
    
    // If still no organization ID found, use the logged-in user's organization
    if (organizationId === 0) {
        organizationId = resolveOrganizationIdFromAuth(componentDataDestService);
        if (organizationId > 0) {
        }
    }
    
    
    // Create organization DTO - ensure id is an integer
    const organizationDTO: OrganizationDTO = {
        id: organizationId,
        org_name: data.organization_name || data.organization?.org_name || '', // Will be set by backend
        data: null // Will be set by backend
    };

    // Ensure organization_id is also set as a separate field for backend compatibility
    data.organization_id = organizationId;
    data.organizations_id = organizationId;

    // Create location DTO if location is provided
    let locationDTO: LocationDTO | undefined;
    if (data.location && data.location !== 'manual') {
        locationDTO = {
            guid: data.location,
            locations_name: '', // Will be populated by backend
            clean_name: '', // Will be populated by backend
            data: null, // Will be populated by backend
            rooms: [],
            users: [],
            registered_percentage: 0,
            progress_percentage: 0,
            ok_percentage: 0,
            firefighter_count: 0
        };
    }

    // Create room DTO if room is provided and using predefined location
    let roomDTO: RoomDTO | undefined;
    if (data.room && data.location !== 'manual') {
        roomDTO = {
            guid: data.room,
            room_name: '', // Will be populated by backend
            location_guid: data.location || '', // Will be populated by backend
            capacity: 0, // Will be populated by backend
            description: '' // Will be populated by backend
        };
    }

    // Handle different event duration types (dates normalized in normalizeEventFormForApi)
    let startDate = data.start_date;
    let endDate = data.end_date || data.start_date;
    
    if (eventDurationType === 'single_day' && data.end_time && !data.end_date) {
        endDate = applyEndTimeToStartDate(String(data.start_date), String(data.end_time));
    }

    const additionalRooms: NonNullable<EventDTO['additional_rooms']> = mapAdditionalRoomsToApi(
        data.additional_rooms,
        { excludeRoomGuid: typeof data.room === 'string' ? data.room : null }
    );
    const organizationResources: NonNullable<EventDTO['organization_resources']> =
        mapOrganizationResourceIdsToApi(data.organization_resource_ids);

    // Convert incoming data to DTO format
    const eventDTO: Partial<EventDTO> = {
        event_type: data.event_type as 'education' | 'course' | 'exercise' | 'activity',
        start_date: startDate,
        end_date: endDate,
        organization: organizationDTO, // Use the organization DTO
        // Also set organizations_id for backward compatibility (ensure it's an integer)
        organizations_id: organizationId,
        visibility: data.visibility as 'public' | 'private' | 'shared',
        catering: mapFormCateringToApi(data.catering),
        is_active: data.event_active === 'active',
        is_shown: data.is_shown === 'shown',
        wait_list_type: data.waitlist as 'room_size' | 'everyone_on_wait_list' | 'no_wait_list',
        price: resolveEventPriceFromFormData(data),
        course_pricing: buildCoursePricingFromFormData(data),
        customer_guid: data.customer_guid || null,
        ...buildCoursePriceMetadataFromFormData(data),
        group_calendar_template_id:
            data.group_calendar_template_id != null &&
            data.group_calendar_template_id !== '' &&
            Number(data.group_calendar_template_id) > 0
                ? Number(data.group_calendar_template_id)
                : null,
        deleted: false,
        guid: data.guid || crypto.randomUUID(),
        created_at: new Date().toISOString(),
        destination_period: data.destination_period || 'current_period' as 'current_period' | 'last_period' | 'both_periods',
        event_duration_type: eventDurationType as EventDTO['event_duration_type'],
        series_share_participants: getSeriesShareParticipants(data),
        user_group_id:
            data.user_group_id != null &&
            data.user_group_id !== '' &&
            Number(data.user_group_id) > 0
                ? Number(data.user_group_id)
                : null,
        
        // Use the location and room DTOs
        predefined_location: locationDTO,
        predefined_room: roomDTO,
        
        manual_location: locationDTO ? undefined : (data.manual_location || undefined),
        manual_location_address: locationDTO ? undefined : (data.manual_location_address || undefined),
        manual_location_room_capacity: data.manual_room_capacity ? Number(data.manual_room_capacity) : undefined,
        ...(additionalRooms.length > 0 ? { additional_rooms: additionalRooms } : {}),
        ...(organizationResources.length > 0
            ? { organization_resources: organizationResources }
            : {}),
        description: {
            event_id: 0, // Will be set by backend
            short_description: {
                da: {
                    language_code: 'da',
                    translation_text: data.descriptions?.find((d: any) => d.language_code === 'da')?.short_description || data.short_description || ''
                }
            },
            long_description: {
                da: {
                    language_code: 'da',
                    translation_text: data.descriptions?.find((d: any) => d.language_code === 'da')?.long_description || data.description || ''
                }
            }
        },
        participants: [],
        materials: [],
        documents: [],
        sub_events: [], // Sub-events are now handled as separate events with parent_event_id
        items: [
            ...(data.event_modules?.event_modules || []).map((moduleId: number) => ({
                id: 0, // Will be set by backend
                event_id: 0, // Will be set by backend
                item_type: 'modules',
                item_id: moduleId,
                deleted: false
            })),
            ...(data.event_subtypes?.event_subtypes || []).map((subtypeId: number) => ({
                id: 0, // Will be set by backend
                event_id: 0, // Will be set by backend
                item_type: 'subtypes',
                item_id: subtypeId,
                deleted: false
            }))
        ]
    };

    // Process sub-events based on event duration type
    let processedSubEvents = [];
    if ((eventDurationType === 'multiple_dates' || eventDurationType === 'multiple_dates_locations') && data.sub_events && data.sub_events.length > 0) {
        processedSubEvents = data.sub_events.map((subEvent: any, index: number) => {
            const subEventData: any = {
                sub_event_date: subEvent.sub_event_date,
                sub_event_start_time: subEvent.sub_event_start_time,
                sub_event_end_time: subEvent.sub_event_end_time
            };
            
            // Include per-day location when present (series with same or varying locations)
            if (subEvent.sub_event_location) {
                subEventData.sub_event_location = subEvent.sub_event_location;
                subEventData.sub_event_room = subEvent.sub_event_room;
                subEventData.sub_event_manual_location = subEvent.sub_event_manual_location;
                subEventData.sub_event_manual_location_address = subEvent.sub_event_manual_location_address;
                subEventData.sub_event_manual_room_capacity = subEvent.sub_event_manual_room_capacity;
            }
            if (subEvent.sub_event_additional_rooms) {
                subEventData.sub_event_additional_rooms = subEvent.sub_event_additional_rooms;
            }
            if (subEvent.items && Array.isArray(subEvent.items)) {
                subEventData.items = subEvent.items;
            }
            if (subEvent.sub_event_catering) {
                subEventData.sub_event_catering = mapFormCateringToApi(
                    subEvent.sub_event_catering
                );
            }
            
            return subEventData;
        });
    } else {
    }

    // Prepare request body with sub-events data
    const requestBody = { 
        event: eventDTO,
        sub_events: processedSubEvents, // Pass processed sub-events data for backend processing
        multi_day_skip_dates: data.multi_day_skip_dates || [],
    };

    // Send DTO to API
    // Return an Observable that the form will subscribe to
    // The form's isSubmitting flag prevents infinite loops from doComplete() re-triggering onCompleting
    // NOTE: We do NOT subscribe here - the form subscribes, which triggers the HTTP request
    // NOTE: We clear the events cache after successful creation so the calendar reloads fresh data
    return componentDataDestService.http.post(`${environment.apiUrl}/events`, requestBody).pipe(
        catchError(error => {
            console.error("[add_event] Error:", error);
            throw error;
        }),
        switchMap((response: unknown) => {
            const body = response as Record<string, unknown>;
            const payload = (body?.['data'] as Record<string, unknown>) ?? body;
            const guids = extractEventGuidsFromCreateResponse(payload);
            if (guids.length === 0) {
                return of(response);
            }
            const formData = data as Record<string, unknown>;
            return patchEventCategoryForEventGuids(
                componentDataDestService.http,
                guids,
                categoryPatchData
            ).pipe(
                switchMap(() =>
                    postEventMaterialRequestsForGuids(
                        componentDataDestService.http,
                        guids,
                        formData
                    )
                ),
                switchMap((matRes) => {
                    if (matRes.failed) {
                        throw new Error(matRes.failed);
                    }
                    return of(response);
                })
            );
        }),
        tap(response => {
            // Clear the events cache so the calendar reloads with the new event
            try {
                const store = componentDataDestService.injector.get(Store);
                store.dispatch(EventsActions.clearEventsData());
            } catch (error) {
                console.warn("[add_event] Could not clear events cache:", error);
            }
        }),
        map(response => {
            return response;
        })
    );
}

export interface TemplateCopyInstance {
    copy_start_date: string;
    copy_end_time?: string;
    description?: string;
    short_description?: string;
    sub_event_location?: string;
    sub_event_room?: string;
    sub_event_manual_location?: string;
    sub_event_manual_location_address?: string;
    sub_event_manual_room_capacity?: string | number;
}

function hasCopyValue(value: unknown): boolean {
    return value !== undefined && value !== null && value !== '';
}

/** SurveyJS dualpanel shape for modules (string ids match edit-event / dual_panel API). */
export function eventModulesFormValueFromTemplate(
    templateData: Record<string, unknown>
): { event_modules: string[] } {
    const raw = templateData['event_modules'];
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        const nested = (raw as Record<string, unknown>)['event_modules'];
        if (Array.isArray(nested)) {
            return {
                event_modules: nested
                    .map((id) => String(id))
                    .filter((id) => id !== '' && id !== 'NaN'),
            };
        }
    }
    if (Array.isArray(raw)) {
        return {
            event_modules: raw.map((id) => String(id)).filter((id) => id !== '' && id !== 'NaN'),
        };
    }
    return { event_modules: [] };
}

export function eventModulesFormValueFromEvent(event: EventDTO): { event_modules: string[] } {
    const ids =
        event.items
            ?.filter((item) => item.item_type === 'modules')
            .map((item) => String(item.item_id))
            .filter((id) => id !== '' && id !== 'NaN') ?? [];
    return { event_modules: ids };
}

function getEventTranslationText(
    block?: Record<string, { translation_text?: string }>
): string {
    if (!block) return '';
    const da = block['da']?.translation_text;
    if (da) return da;
    const first = Object.values(block)[0]?.translation_text;
    return first ?? '';
}

/** Form fields to overlay when editing/replacing a cell that already has an event. */
export function buildExistingEventCellFormPatch(event: EventDTO): Record<string, unknown> {
    const patch: Record<string, unknown> = {
        event_modules: eventModulesFormValueFromEvent(event),
    };

    const short = getEventTranslationText(event.description?.short_description);
    const long = getEventTranslationText(event.description?.long_description);
    if (short) patch['short_description'] = short;
    if (long) patch['description'] = long;

    if (event.start_date) {
        patch['copy_start_date'] = String(event.start_date).replace(' ', 'T').slice(0, 16);
    }
    if (event.end_date) {
        const end = String(event.end_date);
        const timePart = end.includes('T') ? end.split('T')[1] : end.split(' ')[1];
        if (timePart) {
            patch['copy_end_time'] = timePart.slice(0, 5);
        }
    }

    return patch;
}

function eventModulesFormValueFromForm(
    formValues: Record<string, unknown> | undefined
): { event_modules: number[] } | null {
    if (!formValues || !Object.prototype.hasOwnProperty.call(formValues, 'event_modules')) {
        return null;
    }
    const raw = formValues['event_modules'];
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        const nested = (raw as Record<string, unknown>)['event_modules'];
        if (Array.isArray(nested)) {
            return {
                event_modules: nested.map((id) => Number(id)).filter((id) => Number.isFinite(id)),
            };
        }
    }
    if (Array.isArray(raw)) {
        return {
            event_modules: raw.map((id) => Number(id)).filter((id) => Number.isFinite(id)),
        };
    }
    return { event_modules: [] };
}

/** Build a single-day event payload from template data and one copy instance. */
export function buildEventDataFromTemplateCopy(templateData: any, instance: TemplateCopyInstance): any {
    const eventData = { ...templateData };
    eventData.event_duration_type = 'single_day';
    eventData.start_date = instance.copy_start_date;
    eventData.end_time = instance.copy_end_time || templateData.end_time;
    delete eventData.sub_events;
    delete eventData.end_date;

    if (hasCopyValue(instance.sub_event_location)) {
        if (instance.sub_event_location === 'manual') {
            eventData.location = 'manual';
            eventData.manual_location = instance.sub_event_manual_location || 'Andet';
            const manualAddress = instance.sub_event_manual_location_address?.trim();
            eventData.manual_location_address = manualAddress || 'Ikke angivet';
            eventData.manual_room_capacity = instance.sub_event_manual_room_capacity ?? templateData.manual_room_capacity;
            eventData.room = '';
        } else {
            eventData.location = instance.sub_event_location;
            eventData.room = instance.sub_event_room ?? '';
            delete eventData.manual_location;
            delete eventData.manual_location_address;
            if (hasCopyValue(instance.sub_event_manual_room_capacity)) {
                eventData.manual_room_capacity = instance.sub_event_manual_room_capacity;
            } else {
                delete eventData.manual_room_capacity;
            }
        }
    }

    if (hasCopyValue(instance.description)) {
        eventData.description = instance.description;
    }
    if (hasCopyValue(instance.short_description)) {
        eventData.short_description = instance.short_description;
    }

    return eventData;
}

/** Default form values when creating multiple events from a template (wizard). */
export function buildMultiCopyFormDefaults(templateData: Record<string, unknown>): Record<string, unknown> {
    const td = templateData;
    return {
        description: td['description'] || '',
        short_description: td['short_description'] || '',
        copy_instances: [buildTemplateCopyInstanceFromTemplate(templateData)]
    };
}

/** Default form values when creating a single event from a template (e.g. group calendar). */
export function buildSingleEventFormDefaults(
    templateData: Record<string, unknown>,
    context: { date: Date; locationKey: string }
): Record<string, unknown> {
    const td = templateData;
    const start = new Date(context.date);
    if (td['start_date']) {
        const tplStart = new Date(td['start_date'] as string);
        start.setHours(tplStart.getHours(), tplStart.getMinutes(), 0, 0);
    } else {
        start.setHours(9, 0, 0, 0);
    }

    const instance = buildTemplateCopyInstanceFromTemplate(templateData);
    instance.copy_start_date = formatLocalDateTime(start);

    if (context.locationKey === 'other') {
        instance.sub_event_location = 'manual';
        instance.sub_event_manual_location = (td['manual_location'] as string) || 'Andet';
        instance.sub_event_manual_location_address = (td['manual_location_address'] as string) || '';
    } else {
        instance.sub_event_location = context.locationKey;
        instance.sub_event_room = (td['room'] as string) || '';
    }

    return {
        description: td['description'] || '',
        short_description: td['short_description'] || '',
        event_modules: eventModulesFormValueFromTemplate(td),
        ...instance
    };
}

function buildTemplateCopyInstanceFromTemplate(templateData: Record<string, unknown>): TemplateCopyInstance {
    const td = templateData;
    return {
        copy_start_date: (td['start_date'] as string) || '',
        copy_end_time: (td['end_time'] as string) || '13:00',
        sub_event_location: (td['location'] as string) || '',
        sub_event_room: (td['room'] as string) || '',
        sub_event_manual_location: (td['manual_location'] as string) || '',
        sub_event_manual_location_address: (td['manual_location_address'] as string) || '',
        sub_event_manual_room_capacity: td['manual_room_capacity'] as string | number | undefined
    };
}

/** Map flat single-event form values to a copy instance. */
export function templateSingleEventFormToInstance(formValues: Record<string, unknown>): TemplateCopyInstance {
    return {
        copy_start_date: String(formValues['copy_start_date'] || ''),
        copy_end_time: formValues['copy_end_time'] as string | undefined,
        description: String(formValues['description'] || '').trim(),
        short_description: String(formValues['short_description'] || '').trim(),
        sub_event_location: formValues['sub_event_location'] as string | undefined,
        sub_event_room: formValues['sub_event_room'] as string | undefined,
        sub_event_manual_location: formValues['sub_event_manual_location'] as string | undefined,
        sub_event_manual_location_address: formValues['sub_event_manual_location_address'] as string | undefined,
        sub_event_manual_room_capacity: formValues['sub_event_manual_room_capacity'] as string | number | undefined
    };
}

function extractEventGuidFromCreateResponse(response: unknown, fallbackGuid?: string): string | null {
    if (!response || typeof response !== 'object') {
        return fallbackGuid ?? null;
    }
    const row = response as Record<string, unknown>;
    const nested = row['event'];
    const guid =
        (typeof row['guid'] === 'string' && row['guid']) ||
        (typeof nested === 'object' &&
            nested != null &&
            typeof (nested as Record<string, unknown>)['guid'] === 'string' &&
            (nested as Record<string, unknown>)['guid']) ||
        fallbackGuid;
    return typeof guid === 'string' && guid.length > 0 ? guid : null;
}

async function fetchUserGroupMemberUuids(
    http: HttpClient,
    groupId: number,
    locationKey: string
): Promise<string[]> {
    const url =
        locationKey && locationKey !== 'other'
            ? `${environment.apiUrl}/user-groups/${groupId}/members/by-location/${locationKey}`
            : `${environment.apiUrl}/user-groups/${groupId}/members`;
    const res = await firstValueFrom(http.get<unknown>(url));
    const payload =
        res && typeof res === 'object' && 'data' in (res as object)
            ? (res as { data?: { members?: { uuid?: string }[] } }).data ?? res
            : res;
    const members =
        payload && typeof payload === 'object' && 'members' in (payload as object)
            ? (payload as { members?: { uuid?: string }[] }).members ?? []
            : [];
    return members
        .map((m) => m?.uuid)
        .filter((uuid): uuid is string => typeof uuid === 'string' && uuid.length > 0);
}

async function addEventParticipantsWithoutNotifications(
    http: HttpClient,
    eventGuid: string,
    userUuids: string[]
): Promise<void> {
    const participants: EventParticipantDTO[] = userUuids.map((uuid) => ({
        user: {
            uuid,
            first_name: '',
            last_name: '',
            user: null,
            roles: null,
            organizations_id: null,
            locations: null,
            status: null,
        },
        role: 'attendee',
        status: 'pending',
    }));
    await firstValueFrom(
        http.post(`${environment.apiUrl}/events/${eventGuid}/participants`, {
            participants,
            send_notifications: false,
        })
    );
}

/** Create a single hidden group-calendar event from template data for a specific day/location. */
export function createGroupCalendarEvent(
    componentDataDestService: ComponentDataDestService,
    componentDataSourceService: ComponentDataSourceService,
    templateData: Record<string, unknown>,
    date: Date,
    locationKey: string,
    userGroupId?: number | null,
    templateId?: number | null,
    formValues?: Record<string, unknown>
): Promise<unknown> {
    const instance = formValues
        ? templateSingleEventFormToInstance(formValues)
        : (() => {
              const defaults = buildSingleEventFormDefaults(templateData, { date, locationKey });
              return templateSingleEventFormToInstance(defaults);
          })();

    const eventData = buildEventDataFromTemplateCopy(templateData, instance);
    const modulesFromForm = eventModulesFormValueFromForm(formValues);
    if (modulesFromForm) {
        eventData.event_modules = modulesFromForm;
    }
    eventData.is_shown = 'hidden';
    eventData.guid = crypto.randomUUID();

    const orgId = resolveOrganizationIdFromAuth(componentDataDestService);
    if (orgId > 0) {
        eventData.organization_id = orgId;
        eventData.organizations_id = orgId;
        eventData.organization = { id: orgId };
    }

    const resolvedGroupId =
        userGroupId != null && Number(userGroupId) > 0 ? Number(userGroupId) : null;
    const resolvedTemplateId =
        templateId != null && Number(templateId) > 0 ? Number(templateId) : null;
    delete eventData.user_group_id;
    if (resolvedGroupId != null) {
        eventData.user_group_id = resolvedGroupId;
    }
    if (resolvedTemplateId != null) {
        eventData.group_calendar_template_id = resolvedTemplateId;
    }

    const http = componentDataDestService.http;
    return firstValueFrom(add_event(componentDataDestService, componentDataSourceService, eventData)).then(
        async (response) => {
            const guid = extractEventGuidFromCreateResponse(response, eventData.guid);
            if (!guid) {
                return response;
            }
            const patchEvent: Record<string, unknown> = {};
            if (resolvedGroupId != null) {
                patchEvent['user_group_id'] = resolvedGroupId;
            }
            if (resolvedTemplateId != null) {
                patchEvent['group_calendar_template_id'] = resolvedTemplateId;
            }
            if (Object.keys(patchEvent).length > 0) {
                try {
                    await firstValueFrom(
                        http.patch(`${environment.apiUrl}/events/${guid}`, {
                            event: patchEvent,
                        })
                    );
                } catch (error) {
                    console.warn(
                        '[createGroupCalendarEvent] Could not confirm group calendar fields on event:',
                        error
                    );
                }
            }
            if (resolvedGroupId != null) {
                try {
                    const memberUuids = await fetchUserGroupMemberUuids(
                        http,
                        resolvedGroupId,
                        locationKey
                    );
                    if (memberUuids.length > 0) {
                        await addEventParticipantsWithoutNotifications(http, guid, memberUuids);
                    }
                } catch (error) {
                    console.warn(
                        '[createGroupCalendarEvent] Could not add group members as participants:',
                        error
                    );
                }
            }
            return response;
        }
    );
}

/** Create independent single-day events from a template and copy instances (no grouping). */
export function copy_events_from_template(
    componentDataDestService: ComponentDataDestService,
    componentDataSourceService: ComponentDataSourceService,
    templateData: any,
    copyInstances: TemplateCopyInstance[]
) {
    if (!copyInstances?.length) {
        return Promise.resolve([]);
    }

    const eventPromises = copyInstances.map((instance, index) => {
        const eventData = buildEventDataFromTemplateCopy(templateData, instance);
        return firstValueFrom(add_event(componentDataDestService, componentDataSourceService, eventData));
    });

    return Promise.all(eventPromises).then((results) => {
        try {
            const store = componentDataDestService.injector.get(Store);
            store.dispatch(EventsActions.clearEventsData());
        } catch (error) {
            console.warn('[copy_events_from_template] Could not clear events cache:', error);
        }
        return results;
    });
}

/** @deprecated Use copy_events_from_template instead */
export function add_multiple_events_from_template(
    componentDataDestService: ComponentDataDestService,
    componentDataSourceService: ComponentDataSourceService,
    templateData: any,
    selectedDates: Date[]
) {
    const copyInstances: TemplateCopyInstance[] = selectedDates.map((date) => {
        const startTime = templateData.start_date ? new Date(templateData.start_date) : new Date(date);
        const newStart = new Date(date);
        newStart.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
        return {
            copy_start_date: formatLocalDateTime(newStart),
            copy_end_time: templateData.end_time
        };
    });
    return copy_events_from_template(
        componentDataDestService,
        componentDataSourceService,
        templateData,
        copyInstances
    );
} 